import { useState, useEffect, useCallback } from 'react';
import { Typography, CircularProgress, Box, Button, Chip, Stack, Alert, Divider } from '@mui/material';
import axios from 'axios';
import config from '../config/config.json';
import { useAuth } from '../context/AuthContext';
import StatsCard from '../components/Dashboard/StatsCard';
import UserChart from '../components/Dashboard/UserChart';
import BroadcastEmailModal from '../components/Dashboard/BroadcastEmailModal';
import PeopleIcon from '@mui/icons-material/People';
import TodayIcon from '@mui/icons-material/Today';
import PrayerIcon from '@mui/icons-material/MosqueOutlined';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import EmailIcon from '@mui/icons-material/Email';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import { WorshipChart, RecentTables } from '../components/Dashboard/OverviewDetails';
import moment from 'moment';

const cardSx = {
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '24px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  border: '1px solid #E5E7EB',
};

function InsightRow({ label, value, helper, tone = 'primary' }) {
  const palette = {
    primary: { bg: '#EFF6FF', color: '#2563EB' },
    success: { bg: '#ECFDF5', color: '#059669' },
    warning: { bg: '#FFF7ED', color: '#C2410C' },
    danger: { bg: '#FEF2F2', color: '#DC2626' },
    neutral: { bg: '#F8FAFC', color: '#475569' },
  }[tone] || { bg: '#EFF6FF', color: '#2563EB' };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '14px',
        backgroundColor: palette.bg,
        border: '1px solid #E5E7EB',
      }}
    >
      <Typography sx={{ color: '#6B7280', fontSize: '13px', fontWeight: 600, mb: 0.75 }}>
        {label}
      </Typography>
      <Typography sx={{ color: palette.color, fontSize: '24px', fontWeight: 800, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {helper ? (
        <Typography sx={{ color: '#94A3B8', fontSize: '12px', mt: 0.75 }}>
          {helper}
        </Typography>
      ) : null}
    </Box>
  );
}

function DashboardPage() {
  const { token, admin } = useAuth();
  const [chartDays, setChartDays] = useState(7);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const response = await axios.get(`${config.api_base}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.statusCode === 200) {
        setStats(response.data.data);
      } else {
        setErrorMessage('Dashboard verileri alınamadı.');
      }
    } catch (error) {
      console.error('Stats fetch error:', error);
      setErrorMessage(error?.response?.data?.message || 'Dashboard verileri alınamadı.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorMessage && !stats) {
    return (
      <Box>
        <Alert severity="error">{errorMessage}</Alert>
      </Box>
    );
  }

  const overview = stats?.overview || {};
  const userGrowth = stats?.userGrowth || [];
  const platformStats = stats?.platformStats || { ios: 0, android: 0, web: 0, unknown: 0, mobile: 0 };
  const subscriptionStats = stats?.subscriptionStats || {};
  const supportStats = stats?.supportStats || {};
  const socialStats = stats?.socialStats || {};
  const engagementStats = stats?.engagementStats || {};
  const activeRate = overview.totalUsers > 0
    ? Math.round((overview.activeUsersToday / overview.totalUsers) * 100)
    : 0;
  const paidUsers = (subscriptionStats.standard || 0) + (subscriptionStats.pro || 0);
  const paidRate = overview.totalUsers > 0
    ? Math.round((paidUsers / overview.totalUsers) * 100)
    : 0;

  return (
    <Box className="overview-page">
      <div className="overview-welcome"><div><h1>Hoş geldiniz, {admin?.name || 'Admin'}! <span role="img" aria-label="Merhaba">👋</span></h1><p>İbadet Takibi uygulamasının genel durumunu, kullanıcı etkileşimini ve temel metrikleri<br className="overview-desktop-break" /> buradan takip edebilirsiniz.</p></div><div className="overview-date"><span>{moment().format('DD MMMM YYYY')}</span><small>Güncel sistem özeti</small></div></div>

      {errorMessage ? <Alert severity="warning" sx={{ mb: 3 }}>{errorMessage}</Alert> : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(6, 1fr)' },
          gap: 1.75,
          mb: 2.25,
        }}
      >
        <StatsCard
          title="Toplam Kullanıcı"
          value={overview.totalUsers}
          subtitle="Kayıtlı hesap"
          icon={<PeopleIcon sx={{ fontSize: 24 }} />}
          tone="primary"
        />
        <StatsCard
          title="Bugün Aktif"
          value={overview.activeUsersToday}
          subtitle="Bugün işlem yapan"
          icon={<TodayIcon sx={{ fontSize: 24 }} />}
          tone="success"
        />
        <StatsCard
          title="Haftalık Yeni"
          value={overview.newUsersThisWeek}
          subtitle="Bu hafta katılan"
          icon={<PersonAddAlt1OutlinedIcon sx={{ fontSize: 24 }} />}
          tone="neutral"
        />
        <StatsCard
          title="Toplam Namaz"
          value={overview.totalPrayers}
          subtitle="Sistemdeki kayıt"
          icon={<PrayerIcon sx={{ fontSize: 24 }} />}
          tone="warning"
        />
        <StatsCard
          title="Toplam Oruç"
          value={overview.totalFastings}
          subtitle="Sistemdeki kayıt"
          icon={<FastfoodIcon sx={{ fontSize: 24 }} />}
          tone="success"
        />
        <StatsCard
          title="Açık Destek"
          value={supportStats.openTickets}
          subtitle="Açık / işlemde ticket"
          icon={<SupportAgentOutlinedIcon sx={{ fontSize: 24 }} />}
          tone="danger"
        />
      </Box>

      <div className="overview-charts">
        <UserChart data={userGrowth} days={chartDays} onDaysChange={setChartDays} />
        <WorshipChart overview={overview} />
      </div>
      <RecentTables token={token} />

      <details className="overview-operations"><summary>Detaylı operasyon metrikleri</summary>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 3, mt: 2 }}>

        <Box sx={cardSx}>
          <Stack spacing={2.5}>
            <Box>
              <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#111827', mb: 0.5 }}>
                Operasyon Özeti
              </Typography>
              <Typography sx={{ color: '#6B7280' }}>
                Günlük yönetim tarafında hızlı tarama için özet metrikler.
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <InsightRow
                label="Bugün aktif oranı"
                value={`%${activeRate}`}
                helper="Toplam kullanıcıya göre"
                tone="primary"
              />
              <InsightRow
                label="Platform"
                value={`${platformStats.mobile || 0} mobil`}
                helper={`${platformStats.ios || 0} iOS · ${platformStats.android || 0} Android · ${platformStats.web || 0} web`}
                tone="neutral"
              />
              <InsightRow
                label="Ücretli dönüşüm"
                value={`%${paidRate}`}
                helper={`${paidUsers || 0} ücretli kullanıcı`}
                tone="warning"
              />
              <InsightRow
                label="Yakın biten abonelik"
                value={subscriptionStats.expiringSoon || 0}
                helper={`${subscriptionStats.autoRenewalOn || 0} otomatik yenileme açık`}
                tone="danger"
              />
              <InsightRow
                label="Açık ticket"
                value={supportStats.openTickets || 0}
                helper={`${supportStats.highPriorityTickets || 0} yüksek öncelik`}
                tone="success"
              />
            </Box>

            <Divider />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <InsightRow
                label="Aile bağlantısı"
                value={socialStats.acceptedFamilyLinks || 0}
                helper={`${socialStats.pendingFamilyInvites || 0} bekleyen davet`}
                tone="primary"
              />
              <InsightRow
                label="Arkadaşlık"
                value={socialStats.acceptedFriendships || 0}
                helper={`${socialStats.pendingFriendships || 0} bekleyen istek`}
                tone="neutral"
              />
              <InsightRow
                label="Aktif hatim"
                value={engagementStats.activeHatims || 0}
                helper={`${engagementStats.completedHatims || 0} tamamlanan`}
                tone="success"
              />
              <InsightRow
                label="Haftalık etkileşim"
                value={engagementStats.moodEntriesThisWeek || 0}
                helper={`${engagementStats.tesbihSessionsThisWeek || 0} tesbih oturumu · ${engagementStats.nafileEntriesThisWeek || 0} nafile kaydı`}
                tone="success"
              />
            </Box>

            <Box
              sx={{
                p: 2.5,
                borderRadius: '16px',
                border: '1px solid #E5E7EB',
                backgroundColor: '#F8FAFC',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    backgroundColor: '#EFF6FF',
                    color: '#2563EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AdminPanelSettingsOutlinedIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: '#111827' }}>Yönetim Notu</Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '13px' }}>Hızlı operasyon aksiyonları</Typography>
                </Box>
              </Stack>
              <Typography sx={{ color: '#475569', lineHeight: 1.7 }}>
                Dashboard artık sadece temel sayıları değil; ücretli dönüşüm, destek yükü, sosyal ağ büyüklüğü ve ibadet etkileşimini aynı akışta gösteriyor.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 2 }}>
                <Chip
                  icon={<FavoriteBorderOutlinedIcon />}
                  label={`${engagementStats.tesbihCountThisWeek || 0} haftalık tesbih`}
                  sx={{ backgroundColor: '#EFF6FF', color: '#2563EB', fontWeight: 600 }}
                />
                <Chip
                  icon={<EmojiEventsOutlinedIcon />}
                  label={`Ort. seviye ${engagementStats.avgLevel || 0}`}
                  sx={{ backgroundColor: '#ECFDF5', color: '#059669', fontWeight: 600 }}
                />
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Box>
      </details>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}><Button startIcon={<EmailIcon />} onClick={() => setEmailModalOpen(true)} sx={{ textTransform: 'none' }}>Toplu e-posta gönder</Button></Box>

      <BroadcastEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        token={token}
      />
    </Box>
  );
}

export default DashboardPage;
